import { AlertCircle, Building2, Download, LogIn } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import type { Lab } from '../lib/lab';
import { isNativeShell } from '../lib/nativeBridge';
import { Button, Spinner } from './ui';
import PoweredBy from './PoweredBy';

/**
 * The way in.
 *
 * Two routes, and both are third-party on purpose: Apple is required alongside
 * Google by App Store guideline 4.8, which asks that an app offering one
 * third-party login also offer one that collects no more than a name and an
 * address and lets the user withhold even that.
 *
 * Email and password used to sit here too. It existed so App Review had a
 * credential to sign in with, back when Google was the only provider and an
 * OAuth sign-in from a reviewer's unfamiliar device was likely to be
 * challenged. Sign in with Apple solves that better — a reviewer uses their own
 * Apple ID, gets a real account, and the trial credits let them exercise the
 * whole app — so the password path is gone rather than kept around as a second
 * way to lose an account.
 *
 * No lab code is asked for here. Anyone can sign in; which lab they belong to
 * is the next screen, and both answers there are self-serve.
 */
export default function SignIn({
  lab,
  onChangeLab,
  onEnterLabCode,
}: {
  /** The lab this device was already pointed at, if any. */
  lab: Lab | null;
  onChangeLab: () => void;
  onEnterLabCode: () => void;
}) {
  const androidShell = isNativeShell() && /Android/i.test(navigator.userAgent);
  const { signIn, signInWithApple, error, signingIn } = useAuth();

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">
            Blisx<span className="text-accent-400">Bench</span>
          </h1>
          <p className="mt-2 text-sm text-ink-400">
            Capture-to-spreadsheet for lab measurements.
          </p>
        </div>

        {/* Only shown once a code has actually been entered on this device. */}
        {lab && (
          <div className="mb-6 flex items-center gap-2.5 rounded-lg border border-ink-700 bg-ink-900 px-3 py-2.5">
            <Building2 size={16} className="shrink-0 text-accent-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{lab.name}</p>
              <p className="truncate text-xs text-ink-400">{lab.organisation}</p>
            </div>
            <button
              onClick={onChangeLab}
              className="shrink-0 rounded-md px-2 py-1 text-xs text-ink-400 hover:bg-ink-800 hover:text-ink-100"
            >
              Change
            </button>
          </div>
        )}

        <div className="space-y-2.5">
          {/* Not in the Android app: native Sign in with Apple exists only on
              iOS, so the button there could only ever fail. Guideline 4.8 is
              an App Store rule; the website and the iOS app still show it. */}
          {!androidShell && (
            <>
              {/* Apple's Human Interface Guidelines fix this button's look: the Apple
                  logo, on white or black, never recoloured. A generic icon on the
                  accent colour is a known App Review rejection. */}
              <button
                type="button"
                onClick={signInWithApple}
                disabled={signingIn}
                className="flex w-full items-center justify-center gap-2.5 rounded-lg bg-white px-5 py-3 text-base font-medium text-black pointer-coarse:min-h-12 transition-colors hover:bg-neutral-200 disabled:opacity-60"
              >
                {signingIn ? <Spinner className="size-4" /> : <AppleLogo />}
                Continue with Apple
              </button>
            </>
          )}

          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            icon={<LogIn size={18} />}
            disabled={signingIn}
            onClick={signIn}
          >
            Continue with Google
          </Button>
        </div>

        {error && (
          <div className="mt-4 flex gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <p className="mt-5 flex justify-center">
          <PoweredBy />
        </p>
        <p className="mt-3 text-center text-xs text-ink-400">
          Your data is private to your account until you share a project.
        </p>

        {!lab && (
          <p className="mt-4 text-center text-sm text-ink-400">
            Joining an existing lab?{' '}
            <button
              onClick={onEnterLabCode}
              className="font-medium text-accent-400 underline-offset-2 hover:underline"
            >
              Enter its code
            </button>
          </p>
        )}

        {!isNativeShell() && (
          <a
            href="/download"
            className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-ink-700 py-2.5 text-sm text-ink-300 transition-colors hover:border-ink-600 hover:text-ink-100"
          >
            <Download size={15} />
            Download App
          </a>
        )}

        <p className="mt-4 flex justify-center gap-4 text-xs text-ink-400">
          <a href="/privacy" className="hover:text-ink-100">Privacy</a>
          <a href="/terms" className="hover:text-ink-100">Terms</a>
          <a href="/support" className="hover:text-ink-100">Support</a>
        </p>
      </div>
    </div>
  );
}

function AppleLogo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z" />
    </svg>
  );
}
